import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { listV2Flows } from '../../api/studioV2.api';
import { mergeV2DefinitionSettings, STUDIO_V2_DEFAULT_FLOW_SETTINGS } from './studioV2DefaultFlowSettings';

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft', hint: 'Still being built' },
  { value: 'published', label: 'Ready to use', hint: 'Apps can rely on it' },
  { value: 'archived', label: 'Archived', hint: 'Kept for reference' },
];

const ON_ERROR_OPTIONS = [
  { value: 'stop', label: 'Stop the run' },
  { value: 'continue', label: 'Skip it and keep going' },
  { value: 'retry', label: 'Try the step again' },
  { value: 'skip', label: 'Hand over to the error flow' },
];

/** Settings shown under "More options": [key, label, help]. */
const MORE_SWITCHES = [
  ['enableParallelExecution', 'Run independent branches at the same time', 'Faster when steps do not depend on each other.'],
  ['enableVariableSubstitution', 'Fill in {{placeholders}} in text', 'Replaces {{name}} with values from earlier steps.'],
  ['persistStateBetweenRuns', 'Remember data between runs', null],
  ['autoSaveState', 'Save progress while running', null],
  ['autoSaveStateVariables', 'Save values as they change', null],
  ['enableStateValidation', 'Check data against the data shape', 'Uses the State schema from the Advanced tabs.'],
  ['enableStateEncryption', 'Encrypt stored data', null],
  ['enableExecutionLogging', 'Keep a detailed log of each run', null],
  ['logAllErrors', 'Record every error', null],
  ['enableDebugMode', 'Include extra debugging details', null],
  ['enableSandboxedExecution', 'Run custom code in a sandbox', null],
  ['requireAuthentication', 'Only signed-in people can run it', null],
  ['enableCaching', 'Reuse results for identical input', null],
  ['optimizeMemoryUsage', 'Use less memory', null],
  ['enableApiCalls', 'Allow calls to web services', null],
  ['enableWebhookNotifications', 'Send webhook notifications', null],
  ['enableEmailNotifications', 'Send email notifications', null],
];

function NumberSetting({
  label, help, value, unit, onCommit, scale = 1, min = 0,
}) {
  const shown = value == null ? '' : String(Math.round((Number(value) / scale) * 100) / 100);
  const [draft, setDraft] = useState(shown);
  useEffect(() => { setDraft(shown); }, [shown]);
  const commit = () => {
    const n = Number(draft);
    if (!Number.isFinite(n) || draft === '') {
      setDraft(shown);
      return;
    }
    const next = Math.max(min, n) * scale;
    if (next !== Number(value)) onCommit(Math.round(next));
  };
  return (
    <TextField
      label={label}
      type="number"
      size="small"
      fullWidth
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
      helperText={help}
      inputProps={{ min }}
      InputProps={unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined}
    />
  );
}

function Section({ title, description, children }) {
  return (
    <Box>
      <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
      {description && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{description}</Typography>
      )}
      {!description && <Box sx={{ mb: 1.5 }} />}
      {children}
    </Box>
  );
}

/**
 * Plain-language flow settings: name, status, time limits and failure handling, with rarely used switches collapsed.
 */
export default function StudioV2FlowSettingsEditor({
  text, setText, currentFlowMongoId, readOnly = false,
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState('draft');
  const [settings, setSettings] = useState(() => ({ ...STUDIO_V2_DEFAULT_FLOW_SETTINGS }));
  const [flows, setFlows] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    listV2Flows().then(({ flows: list }) => {
      setFlows(Array.isArray(list) ? list : []);
    }).catch(() => setFlows([]));
  }, []);

  useEffect(() => {
    try {
      const d = JSON.parse(text || '{}');
      setName(d.name != null ? String(d.name) : '');
      setDescription(d.description != null ? String(d.description) : '');
      setStatus(STATUS_OPTIONS.some((o) => o.value === d.status) ? d.status : 'draft');
      setSettings(mergeV2DefinitionSettings(d.settings));
      setError(null);
    } catch {
      setError('The flow has a formatting problem, so settings cannot be shown. Fix it in the JSON view (Advanced).');
    }
  }, [text]);

  const commitTop = (partial) => {
    try {
      const d = JSON.parse(text || '{}');
      setText(JSON.stringify({ ...d, ...partial }, null, 2));
    } catch (e) {
      setError(e.message);
    }
  };

  const commitSettings = (partial) => {
    try {
      const d = JSON.parse(text || '{}');
      const merged = mergeV2DefinitionSettings({ ...(d.settings || {}), ...partial });
      setSettings(merged);
      setText(JSON.stringify({ ...d, settings: merged }, null, 2));
    } catch (e) {
      setError(e.message);
    }
  };

  const flowOptions = flows.filter((f) => String(f._id) !== String(currentFlowMongoId || ''));
  const onError = settings.onError || 'stop';

  if (error) return <Alert severity="warning">{error}</Alert>;

  return (
    <Stack spacing={4} sx={{ maxWidth: 760, opacity: readOnly ? 0.85 : 1 }}>
      <Section title="About this flow" description="How the flow appears in lists and when you connect it to an app.">
        <Stack spacing={2}>
          <TextField
            label="Name"
            size="small"
            fullWidth
            value={name}
            disabled={readOnly}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => commitTop({ name: name.trim() || 'Untitled flow' })}
          />
          <TextField
            label="What does this flow do?"
            size="small"
            fullWidth
            multiline
            minRows={2}
            value={description}
            disabled={readOnly}
            placeholder="For example: screens a candidate's answers and suggests a next step"
            onChange={(e) => setDescription(e.target.value)}
            onBlur={() => commitTop({ description })}
          />
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>Status</Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={status}
              disabled={readOnly}
              onChange={(_e, v) => {
                if (!v) return;
                setStatus(v);
                commitTop({ status: v });
              }}
            >
              {STATUS_OPTIONS.map((o) => (
                <ToggleButton key={o.value} value={o.value} sx={{ textTransform: 'none', px: 2 }} title={o.hint}>
                  {o.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Stack>
      </Section>

      <Section title="Time limits" description="Stop runs that take too long, so apps never wait forever.">
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <NumberSetting
            label="Stop a run after"
            unit="seconds"
            value={settings.maxExecutionTime}
            onCommit={(v) => commitSettings({ maxExecutionTime: v })}
            help="The whole run, from Start to Finish."
          />
          <NumberSetting
            label="Give each step up to"
            unit="seconds"
            scale={1000}
            value={settings.executionTimeout}
            onCommit={(v) => commitSettings({ executionTimeout: v })}
            help="A single step that takes longer is stopped."
          />
          <NumberSetting
            label="Web requests time out after"
            unit="seconds"
            value={settings.defaultApiTimeout}
            onCommit={(v) => commitSettings({ defaultApiTimeout: v })}
          />
          <NumberSetting
            label="Resume a paused run within"
            unit="seconds"
            value={settings.langgraphContinuationTimeout}
            onCommit={(v) => commitSettings({ langgraphContinuationTimeout: v })}
            help="How long a paused run keeps its place before picking up again."
          />
          <NumberSetting
            label="Background work time limit"
            unit="seconds"
            value={settings.backgroundTaskTimeout}
            onCommit={(v) => commitSettings({ backgroundTaskTimeout: v })}
          />
        </Box>
      </Section>

      <Section title="When a step fails" description="What the flow should do if one of its steps runs into a problem.">
        <Stack spacing={2}>
          <TextField
            select
            label="If a step fails"
            size="small"
            fullWidth
            value={onError}
            disabled={readOnly}
            onChange={(e) => commitSettings({ onError: e.target.value })}
          >
            {ON_ERROR_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
          {onError === 'retry' && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <NumberSetting
                label="Try again up to"
                unit="times"
                value={settings.maxRetryAttempts}
                onCommit={(v) => commitSettings({ maxRetryAttempts: v })}
              />
              <NumberSetting
                label="Wait between tries"
                unit="seconds"
                value={settings.retryDelay}
                onCommit={(v) => commitSettings({ retryDelay: v })}
              />
            </Box>
          )}
          <TextField
            select
            label="Run this flow when something fails (optional)"
            size="small"
            fullWidth
            value={settings.errorWorkflowId || ''}
            disabled={readOnly}
            onChange={(e) => commitSettings({ errorWorkflowId: e.target.value || null })}
            helperText="Useful for alerting someone or cleaning up."
          >
            <MenuItem value=""><em>None</em></MenuItem>
            {flowOptions.map((f) => (
              <MenuItem key={f._id} value={String(f._id)}>{f.name || f.definition?.name || 'Untitled flow'}</MenuItem>
            ))}
          </TextField>
        </Stack>
      </Section>

      <Accordion
        disableGutters
        elevation={0}
        sx={{
          border: 1, borderColor: 'divider', borderRadius: 2, '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box>
            <Typography variant="subtitle2" fontWeight={700}>More options</Typography>
            <Typography variant="caption" color="text.secondary">Memory, logging, security and limits. The defaults suit most flows.</Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Stack spacing={0.5}>
            {MORE_SWITCHES.map(([key, label, help]) => (
              <FormControlLabel
                key={key}
                disabled={readOnly}
                control={<Switch size="small" checked={!!settings[key]} onChange={(e) => commitSettings({ [key]: e.target.checked })} />}
                label={(
                  <Box>
                    <Typography variant="body2">{label}</Typography>
                    {help && <Typography variant="caption" color="text.secondary">{help}</Typography>}
                  </Box>
                )}
                sx={{ alignItems: help ? 'flex-start' : 'center', '& .MuiSwitch-root': { mt: help ? 0.25 : 0 } }}
              />
            ))}
          </Stack>
          <Box sx={{
            display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2.5,
          }}
          >
            <NumberSetting label="Max steps running at once" value={settings.maxConcurrentNodes} onCommit={(v) => commitSettings({ maxConcurrentNodes: v })} min={1} />
            <NumberSetting label="Max runs per minute" value={settings.apiRateLimit} onCommit={(v) => commitSettings({ apiRateLimit: v })} />
            <NumberSetting label="Clear stored data after" unit="hours" value={settings.stateCleanupInterval} onCommit={(v) => commitSettings({ stateCleanupInterval: v })} />
            <NumberSetting label="Max stored data" unit="MB" value={settings.maxStateSize} onCommit={(v) => commitSettings({ maxStateSize: v })} />
          </Box>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}
